import { Button } from '@bb/ui';
import { WIZARD_STEPS, stepLabel, type WizardIssue, type WizardStepId } from './steps.ts';

const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

/**
 * The wizard's step tracker (BB-97), above and below the step: each step with its number and state — problems that
 * block the launch, warnings, or nothing to fix.
 */
export function StepTracker({ label, current, issues, onSelect }: { label: string; current: WizardStepId; issues: WizardIssue[]; onSelect: (step: WizardStepId) => void }) {
  return (
    <nav aria-label={label}>
      <ol className="canvas-wizard__steps">
        {WIZARD_STEPS.map((s, i) => {
          const errors = issues.filter((x) => x.step === s.id && x.severity === 'error').length;
          const warnings = issues.filter((x) => x.step === s.id && x.severity === 'warning').length;
          const state = errors ? 'error' : warnings ? 'warning' : 'ok';
          return (
            <li key={s.id}>
              <button type="button" className={`canvas-wizard__step campaign-wizard__step--${state}`} aria-current={s.id === current ? 'step' : undefined} onClick={() => onSelect(s.id)}>
                <span className="canvas-wizard__number" aria-hidden="true">
                  {i + 1}
                </span>
                {s.label}
                {errors > 0 && (
                  <>
                    {' · '}
                    <span className="canvas-wizard__count">{plural(errors, 'problem')}</span>
                  </>
                )}
                {errors === 0 && warnings > 0 && (
                  <>
                    {' · '}
                    <span className="campaign-wizard__warnings">{plural(warnings, 'warning')}</span>
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Problems grouped by the step that fixes them, each group with a way to that step. */
export function IssueGroups({ issues, onGoTo, label }: { issues: WizardIssue[]; onGoTo: (step: WizardStepId) => void; label: string }) {
  const steps = WIZARD_STEPS.map((s) => s.id).filter((id) => issues.some((i) => i.step === id));
  return (
    <div className="canvas-wizard__issues" aria-label={label} role="group">
      {steps.map((step) => (
        <section key={step} aria-label={`${stepLabel(step)} problems`}>
          <h4>{stepLabel(step)}</h4>
          <ul>
            {issues
              .filter((i) => i.step === step)
              .map((issue, k) => (
                <li key={`${issue.path}-${k}`} className={issue.severity === 'error' ? 'field-error' : 'hint'}>
                  {issue.severity === 'warning' ? 'Warning: ' : ''}
                  {issue.message}
                </li>
              ))}
          </ul>
          {step !== 'review' && (
            <Button size="sm" variant="ghost" onClick={() => onGoTo(step)}>
              Go to {stepLabel(step)}
            </Button>
          )}
        </section>
      ))}
    </div>
  );
}
